import type { ActionCommandDef, ParsedActionCommand } from './types';

/**
 * Maximum number of suggestion rows the palette will surface for a
 * non-empty parsed name. Mirrors `MAX_SUGGESTIONS` in the slash
 * matcher so the visual density matches.
 */
const MAX_SUGGESTIONS = 8;

/**
 * Score a single action def against a parsed name. Higher is better.
 * Returns `null` for non-matches so the matcher can drop them.
 *
 * Scoring tiers mirror the slash matcher:
 *   3 — id is an exact prefix of the typed name OR vice versa
 *   2 — any alias is a prefix match (either direction)
 *   1 — id contains the typed name as a substring (after position 0)
 *   0 — alias contains the typed name as a substring
 */
function scoreActionCommand(
  def: ActionCommandDef,
  name: string,
): number | null {
  if (!name) return 0;
  const id = def.id;

  if (id.startsWith(name) || name.startsWith(id)) return 3;
  for (const alias of def.aliases ?? []) {
    if (alias.startsWith(name) || name.startsWith(alias)) return 2;
  }
  if (id.includes(name)) return 1;
  for (const alias of def.aliases ?? []) {
    if (alias.includes(name)) return 0;
  }
  return null;
}

/**
 * Resolve a parsed input to ranked action suggestions. The empty-
 * name case (just `//` typed) returns the full catalog in
 * registration order — what the palette renders right after the
 * second slash is pressed.
 *
 * Stable sort: ties break by the def's original position in `defs`,
 * which mirrors registration order.
 */
export function matchActionCommands({
  parsed,
  defs,
}: {
  parsed: ParsedActionCommand;
  defs: readonly ActionCommandDef[];
}): ActionCommandDef[] {
  if (parsed.name === '') return [...defs];

  const scored: { def: ActionCommandDef; score: number; idx: number }[] = [];
  defs.forEach((def, idx) => {
    const score = scoreActionCommand(def, parsed.name);
    if (score === null) return;
    scored.push({ def, score, idx });
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.idx - b.idx;
  });

  return scored.slice(0, MAX_SUGGESTIONS).map(s => s.def);
}

/**
 * Resolve a parsed input to a *committed* action — i.e. one whose
 * id or alias exactly equals the parsed name. This is what the
 * palette uses to decide between catalog mode and arg-suggestion
 * mode.
 */
export function findCommittedActionCommand({
  parsed,
  defs,
}: {
  parsed: ParsedActionCommand;
  defs: readonly ActionCommandDef[];
}): ActionCommandDef | undefined {
  if (parsed.name === '') return undefined;
  return defs.find(
    def => def.id === parsed.name || (def.aliases ?? []).includes(parsed.name),
  );
}
